import { notificationManager } from './notification-manager.js';
import { supabase } from './supabase.js';

export const notificationScheduler = {
  // Main scheduler function that would be called by cron job every 15 minutes
  async processScheduledNotifications() {
    try {
      console.log('Processing scheduled notifications...');
      
      // Check for planning reminders
      await notificationManager.checkPlanningReminders();
      
      // Check for daily digests
      await notificationManager.sendDailyDigests();
      
      // Process any pending queued notifications
      await this.processPendingQueue();
      
      console.log('Scheduled notifications processed successfully');
    } catch (error) {
      console.error('Error processing scheduled notifications:', error);
    }
  },

  // Process individual queued notifications that are due
  async processPendingQueue() {
    try {
      const { data: pendingNotifications, error } = await supabase
        .from('notification_queue')
        .select('*')
        .eq('status', 'pending')
        .lte('scheduled_for', new Date().toISOString())
        .order('scheduled_for')
        .limit(50); // Process in batches

      if (error) throw error;

      for (const notification of pendingNotifications || []) {
        try {
          await this.processQueuedNotification(notification);
          
          // Mark as sent
          await supabase
            .from('notification_queue')
            .update({ status: 'sent' })
            .eq('id', notification.id);
        } catch (error) {
          console.error(`Error processing notification ${notification.id}:`, error);
          
          // Mark as failed
          await supabase
            .from('notification_queue')
            .update({ status: 'failed' })
            .eq('id', notification.id);
        }
      }
    } catch (error) {
      console.error('Error processing pending queue:', error);
    }
  },

  // Process a single queued notification
  async processQueuedNotification(notification) {
    const { user_id, notification_type, payload } = notification;

    switch (notification_type) {
      case 'daily_planning_reminder':
        await notificationManager.sendIndividualNotification(
          user_id,
          'dailyPlanningReminder',
          payload,
          payload.phoneNumber
        );
        break;

      case 'daily_checkin_reminder':
        await notificationManager.sendIndividualNotification(
          user_id,
          'dailyCheckinReminder',
          payload,
          payload.phoneNumber
        );
        break;

      case 'family_time_invite':
        await notificationManager.sendIndividualNotification(
          user_id,
          'familyTimeInvites',
          payload,
          payload.phoneNumber
        );
        break;

      case 'family_activity_vote':
        await notificationManager.sendIndividualNotification(
          user_id,
          'familyActivityVoting',
          payload,
          payload.phoneNumber
        );
        break;

      case 'confirmed_family_plan':
        await notificationManager.sendIndividualNotification(
          user_id,
          'confirmedFamilyPlans',
          payload,
          payload.phoneNumber
        );
        break;

      case 'new_idea':
        await notificationManager.sendIndividualNotification(
          user_id,
          'newIdeas',
          payload,
          payload.phoneNumber
        );
        break;

      default:
        console.warn(`Unknown notification type: ${notification_type}`);
    }
  },

  // Utility function to schedule a one-time notification
  async scheduleNotification(userId, notificationType, payload, scheduledFor) {
    try {
      const { data, error } = await supabase
        .from('notification_queue')
        .insert([{
          user_id: userId,
          notification_type: notificationType,
          payload,
          scheduled_for: scheduledFor
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error scheduling notification:', error);
      throw error;
    }
  },

  // Schedule daily planning reminders for a user
  async scheduleDailyReminders(userId, preferences) {
    try {
      const now = new Date();
      
      // Schedule planning reminder
      if (preferences.preferences.dailyPlanningReminder.enabled) {
        const [hour, minute] = preferences.preferences.dailyPlanningReminder.time.split(':').map(Number);
        const reminderTime = new Date(now);
        reminderTime.setHours(hour, minute, 0, 0);
        
        // If time has passed today, schedule for tomorrow
        if (reminderTime <= now) {
          reminderTime.setDate(reminderTime.getDate() + 1);
        }

        await this.scheduleNotification(
          userId,
          'daily_planning_reminder',
          {
            phoneNumber: preferences.phone_number,
            userName: preferences.user_name
          },
          reminderTime.toISOString()
        );
      }

      // Schedule check-in reminder
      if (preferences.preferences.dailyCheckinReminder.enabled) {
        const [hour, minute] = preferences.preferences.dailyCheckinReminder.time.split(':').map(Number);
        const reminderTime = new Date(now);
        reminderTime.setHours(hour, minute, 0, 0);
        
        // If time has passed today, schedule for tomorrow
        if (reminderTime <= now) {
          reminderTime.setDate(reminderTime.getDate() + 1);
        }

        await this.scheduleNotification(
          userId,
          'daily_checkin_reminder',
          {
            phoneNumber: preferences.phone_number,
            userName: preferences.user_name
          },
          reminderTime.toISOString()
        );
      }
    } catch (error) {
      console.error('Error scheduling daily reminders:', error);
      throw error;
    }
  },

  // Clean up old processed notifications (run weekly)
  async cleanupOldNotifications() {
    try {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const { error } = await supabase
        .from('notification_queue')
        .delete()
        .in('status', ['sent', 'failed'])
        .lt('scheduled_for', oneWeekAgo.toISOString());

      if (error) throw error;

      console.log('Old notifications cleaned up successfully');
    } catch (error) {
      console.error('Error cleaning up old notifications:', error);
    }
  }
};

// Export a function that can be called by external schedulers
export const runScheduler = async () => {
  await notificationScheduler.processScheduledNotifications();
};